/*
    © Copyright LinkHMS B.V. All rights reserved.
    * Proprietary and confidential.
    * Unauthorized copying of this file, via any medium is strictly prohibited.
*/
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { environment } from '@environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import {
  LANGUAGE_MENU_I18N_SERVICE,
  LANGUAGE_MENU_LANG_LIST,
} from '@shared/components/language-menu/tokens/language-menu.tokens';
import { TimezoneInterceptor } from '@shared/interceptors/timezone.interceptor';
import { RouteReusableStrategy } from '@shared/route-reusable-strategy';
import { AppUpdateService } from '@shared/services/app-update.service';
import { StoreService } from '@shared/services/store.service';
import { GoogleAnalyticsService, NgxGoogleAnalyticsModule } from 'ngx-google-analytics';
import { from, tap } from 'rxjs';

import { LANG_LIST } from './i18n/language-list';
import { ServerErrorInterceptor } from './interceptors/server-error.interceptor';
import { I18nService } from './services/i18n.service';
import { LocaleIdService } from './services/locale-id.service';
import { SharedMetaModule } from './shared-meta/shared-meta.module';

@NgModule({
  exports: [TranslateModule, MatDialogModule, SharedMetaModule],
  imports: [
    CommonModule,
    RouterModule,
    MatDialogModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot(),
    NgxGoogleAnalyticsModule.forRoot(environment.googleAnalyticsKey, [
      {
        command: 'config',
        values: [environment.googleAnalyticsKey, { send_page_view: false }],
      },
    ]),
  ],
  providers: [
    I18nService,
    StoreService,
    GoogleAnalyticsService,
    { provide: LANGUAGE_MENU_I18N_SERVICE, useExisting: I18nService },
    { provide: LANGUAGE_MENU_LANG_LIST, useValue: LANG_LIST },
    { provide: RouteReuseStrategy, useClass: RouteReusableStrategy },
    provideAppInitializer(() => {
      const i18nService = inject(I18nService);
      const appUpdateService = inject(AppUpdateService);

      return from(i18nService.initLanguage()).pipe(tap(() => appUpdateService.start()));
    }),
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: HTTP_INTERCEPTORS, useClass: ServerErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TimezoneInterceptor, multi: true },
    { provide: LOCALE_ID, useClass: LocaleIdService, deps: [I18nService] },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class CoreModule {}
